import React, { useEffect, useState } from "react";
import Chart from "react-apexcharts";
import ApexCharts from "apexcharts";
import jsPDF from "jspdf";
import axiosInstance from "../../../../../api";
import autoTable from "jspdf-autotable";


const EmployeeAssetUsageChart = () => {
  const [year, setYear] = useState("2025");
  const [department, setDepartment] = useState("all");
  const [years, setYears] = useState([]);
  const [chartData, setChartData] = useState({
    labels: [],
    employeeData: [],
    vehicleData: [],
    rawData: null,
  });
  const [noData, setNoData] = useState(false);

  useEffect(() => {
  const currentYear = new Date().getFullYear();
  const startYear = 2020; // or any base year you want
  const yearList = [];
  for (let y = currentYear; y >= startYear; y--) {
    yearList.push(y.toString());
  }
  setYears(yearList);
}, []);


  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await axiosInstance.get("/ems/get-monthly-usage-overview", {
          params: {
            year: year,
            department: department === "all" ? "" : department,
          },
        });

        const apiData = Array.isArray(response.data) ? response.data : response.data?.data;

        if (apiData && apiData.length > 0) {
          const labels = apiData.map((item) => item.month);
          const employeeData = apiData.map((item) => item.employee_count);
          const vehicleData = apiData.map((item) => item.vehicle_count);

          setChartData({
            labels,
            employeeData,
            vehicleData,
            rawData: apiData,
          });

          const allZero = employeeData.every((v) => v === 0) && vehicleData.every((v) => v === 0);
          setNoData(allZero);
        } else {
          setChartData({ labels: [], employeeData: [], vehicleData: [], rawData: null });
          setNoData(true);
        }
      } catch (error) {
        console.error("Error fetching monthly usage overview:", error);
        setChartData({ labels: [], employeeData: [], vehicleData: [], rawData: null });
        setNoData(true);
      }
    };

    fetchData();
  }, [year, department]);

  // Helper function to add data table to PDF
  const addDataTableToPDF = (doc, chartData, startY, pageWidth, pageHeight, margin, year, department) => {
    if (!chartData.labels.length || !chartData.employeeData.length || !chartData.vehicleData.length) {
      return startY;
    }

    const tableWidth = pageWidth - (margin * 2);
    const rowHeight = 20;
    const headerHeight = 25;
    const cellPadding = 8;
    
    let currentY = startY;

    // Table title
    doc.setFont("helvetica", "bold");
    doc.setFontSize(12);
    doc.text("Detailed Monthly Usage Data", margin, currentY);
    currentY += 15;

    // Check if we need a new page before starting table
    const estimatedTableHeight = headerHeight + (chartData.labels.length * rowHeight);
    if (currentY + estimatedTableHeight > pageHeight - margin) {
      doc.addPage();
      currentY = margin;
    }

    // Table headers with background
    doc.setFillColor(240, 240, 240);
    doc.rect(margin, currentY, tableWidth, headerHeight, 'F');
    
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.setTextColor(0, 0, 0);
    
    // Column widths - responsive based on content
    const monthColWidth = tableWidth * 0.3;
    const employeeColWidth = tableWidth * 0.35;
    const vehicleColWidth = tableWidth * 0.35;
    
    // Header text
    doc.text("Month", margin + cellPadding, currentY + headerHeight / 2 + 3);
    doc.text("Employee Count", margin + monthColWidth + cellPadding, currentY + headerHeight / 2 + 3);
    doc.text("Vehicle Count", margin + monthColWidth + employeeColWidth + cellPadding, currentY + headerHeight / 2 + 3);
    
    // Header borders
    doc.setDrawColor(200, 200, 200);
    doc.rect(margin, currentY, tableWidth, headerHeight);
    doc.rect(margin + monthColWidth, currentY, employeeColWidth, headerHeight);
    doc.rect(margin + monthColWidth + employeeColWidth, currentY, vehicleColWidth, headerHeight);
    
    currentY += headerHeight;

    // Table rows
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    
    chartData.labels.forEach((label, index) => {
      // Check if we need a new page
      if (currentY + rowHeight > pageHeight - margin) {
        doc.addPage();
        currentY = margin;
        
        // Redraw headers on new page
        doc.setFillColor(240, 240, 240);
        doc.rect(margin, currentY, tableWidth, headerHeight, 'F');
        doc.setFont("helvetica", "bold");
        doc.setFontSize(10);
        doc.text("Month", margin + cellPadding, currentY + headerHeight / 2 + 3);
        doc.text("Employee Count", margin + monthColWidth + cellPadding, currentY + headerHeight / 2 + 3);
        doc.text("Vehicle Count", margin + monthColWidth + employeeColWidth + cellPadding, currentY + headerHeight / 2 + 3);
        doc.setDrawColor(200, 200, 200);
        doc.rect(margin, currentY, tableWidth, headerHeight);
        doc.rect(margin + monthColWidth, currentY, employeeColWidth, headerHeight);
        doc.rect(margin + monthColWidth + employeeColWidth, currentY, vehicleColWidth, headerHeight);
        currentY += headerHeight;
      }

      // Alternate row colors for readability
      if (index % 2 === 0) {
        doc.setFillColor(250, 250, 250);
        doc.rect(margin, currentY, tableWidth, rowHeight, 'F');
      }

      // Row data
      doc.setTextColor(0, 0, 0);
      doc.text(label.toString(), margin + cellPadding, currentY + rowHeight / 2 + 3);
      doc.text(chartData.employeeData[index].toString(), margin + monthColWidth + cellPadding, currentY + rowHeight / 2 + 3);
      doc.text(chartData.vehicleData[index].toString(), margin + monthColWidth + employeeColWidth + cellPadding, currentY + rowHeight / 2 + 3);
      
      // Row borders
      doc.setDrawColor(200, 200, 200);
      doc.rect(margin, currentY, tableWidth, rowHeight);
      doc.rect(margin + monthColWidth, currentY, employeeColWidth, rowHeight);
      doc.rect(margin + monthColWidth + employeeColWidth, currentY, vehicleColWidth, rowHeight);
      
      currentY += rowHeight;
    });

    return currentY;
  };

  // Enhanced PDF export function with table
  const exportToPDF = async () => {
  try {
    if (!chartData.labels.length || !chartData.employeeData.length || !chartData.vehicleData.length) {
      alert("No data available to export!");
      return;
    }

    // Get chart image from ApexCharts
    const dataURI = await ApexCharts.exec("employee-asset-usage-chart", "dataURI");
    const chartImage = new Image();
    chartImage.src = dataURI.imgURI;
    await new Promise((resolve) => (chartImage.onload = resolve));

    // Create PDF
    const doc = new jsPDF("p", "pt", "a4");
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 20;
    let yOffset = 40;

    // Title
    doc.setFont("helvetica", "bold");
    doc.setFontSize(16);
    doc.text("Monthly Usage Overview Report", pageWidth / 2, yOffset, { align: "center" });
    yOffset += 25;

    // Info
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.text(`Year: ${year}`, margin, yOffset);
    yOffset += 15;
    doc.text(`Department: ${department === "all" ? "All Departments" : department}`, margin, yOffset);
    yOffset += 25;

    // Chart image
    const maxWidth = pageWidth - margin * 2;
    const aspectRatio = chartImage.width / chartImage.height;
    const chartWidth = maxWidth ;
    const chartHeight = chartWidth / aspectRatio * 0.6; //height to 60% for compact layout
    doc.addImage(chartImage, "PNG", margin, yOffset, chartWidth, chartHeight);

    yOffset += chartHeight + 20;

    // --- Add table with graph data ---
    const tableData = chartData.labels.map((label, i) => [
      label,
      chartData.employeeData[i],
      chartData.vehicleData[i],
    ]);

    autoTable(doc, {
      startY: yOffset,
      head: [["Month", "Employee Count", "Vehicle Count"]],
      body: tableData,
      theme: "grid",
      headStyles: { fillColor: [0, 123, 255], textColor: 255, fontStyle: "bold", fontSize: 10 },
      styles: { fontSize: 10, cellPadding: 4, font: "helvetica" },
      margin: { left: margin, right: margin },
      didDrawPage: (data) => {
        // Footer page number
        doc.setFontSize(8);
        doc.text(`Page ${data.pageNumber} of ${doc.internal.getNumberOfPages()}`, pageWidth / 2, pageHeight - 10, { align: "center" });
      },
    });

    // Save PDF
    doc.save(`monthly_usage_overview_${year}_${department}.pdf`);
  } catch (error) {
    console.error("Error exporting PDF:", error);
    alert("Failed to export PDF with chart and table.");
  }
};


  // Download chart as PNG (unchanged from your original)
  const downloadGraph = async () => {
    try {
      const dataURI = await ApexCharts.exec("employee-asset-usage-chart", "dataURI");
      const chartImage = new Image();
      chartImage.src = dataURI.imgURI;

      await new Promise((resolve) => (chartImage.onload = resolve));

      // Device-based responsive width
      const deviceWidth = window.innerWidth;
      const maxWidth = 1000; // Upper cap for desktop
      const exportWidth = Math.min(deviceWidth * 0.95, maxWidth);
      const aspectRatio = chartImage.width / chartImage.height;
      const chartWidth = exportWidth * 0.8; // 80% of canvas width
      const chartHeight = chartWidth / aspectRatio * 0.6; //  height for compact layout
      const headingHeight = 60; // Fixed height for heading

      // Create canvas for heading + chart
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");
      canvas.width = chartWidth;
      canvas.height = chartHeight + headingHeight;

      // Background
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      // Responsive font size for heading
      const fontSize = deviceWidth < 480 ? 14 : 20;
      ctx.fillStyle = "#000000";
      ctx.font = `bold ${fontSize}px "Nunito Sans", sans-serif`;
      ctx.textAlign = "center";

      // Heading text
      const headingText = `Monthly Usage Overview - ${year} (${department === "all" ? "All Departments" : department})`;
      ctx.fillText(headingText, canvas.width / 2, fontSize + 10);

      // Draw chart image
      ctx.drawImage(chartImage, 0, headingHeight, chartWidth, chartHeight);

      // Download PNG
      const link = document.createElement("a");
      link.href = canvas.toDataURL("image/png");
      link.download = `monthly_usage_overview_${year}_${department}.png`;
      link.click();
    } catch (error) {
      console.error("Error downloading graph:", error);
      alert("Failed to download graph.");
    }
  };

  const options = {
    chart: {
      id: "employee-asset-usage-chart",
      fontFamily: '"Nunito Sans", sans-serif',
      height: 350,
      type: "line",
      dropShadow: {
        enabled: true,
        color: "#000",
        top: 18,
        left: 7,
        blur: 10,
        opacity: 0.2,
      },
      toolbar: {
        show: false,
      },
    },
    colors: ["#5C9FFF", "#FFB53B"],
    dataLabels: {
      enabled: true,
    },
    stroke: {
      curve: "smooth",
      width: 2,
    },
    grid: {
      borderColor: "#f1f1f1",
      strokeDashArray: 3,
      row: {
        colors: ["rgba(0,0,0,0.2)", "transparent"],
        opacity: 0.5,
      },
    },
    markers: {
      size: 4,
    },
    xaxis: {
      categories: chartData.labels,
      labels: {
        style: {
          colors: Array(chartData.labels.length).fill("#6b7280"),
          fontSize: "12px",
        },
      },
      axisBorder: {
        show: false,
      },
      axisTicks: {
        show: false,
      },
    },
    yaxis: {
      min: 0,
      labels: {
        style: {
          colors: Array(chartData.labels.length).fill("#6b7280"),
          fontSize: "12px",
        },
        formatter: function (val) {
          return val.toFixed(0);
        },
      },
    },
    tooltip: {
      theme: "light",
    },
    legend: {
      position: "top",
      horizontalAlign: "right",
      floating: true,
      offsetY: -25,
      offsetX: -5,
      labels: {
        colors: "#6b7280",
      },
    },
  };

  const series = [
    {
      name: "Employee Count",
      data: chartData.employeeData,
    },
    {
      name: "Vehicle Count",
      data: chartData.vehicleData,
    },
  ];

  return (
    <>
    <style>{`
    @media(max-width: 575px) {
      .filter-panel{flex-wrap: wrap !important;}
      .filter-left-panel {width: 100%;}
      .filter-left-panel .form-group{width: 100%;}
      .export-btn{width: 100%; }
      .export-main{width: 100% !important;}
      .export-btn .btn{width: 100% !important;}
    }
    `}</style>
      <div className="filter-panel mt-2 d-flex justify-content-between mb-4 flex-wrap gap-2">
        <div className="d-flex align-items-center p-2 flex-wrap filter-left-panel">
          <div className="form-group mb-0">
            <label>Year</label>
            <select
              className="form-select"
              value={year}
              onChange={(e) => setYear(e.target.value)}
              style={{ minWidth: "120px" }}
              aria-label="Select year"
            >
              {years.map((y) => (
                <option key={y} value={y}>
                  {y}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="d-flex align-items-center p-2 export-main">
          <div className="dropdown export-btn">
            <button
              className="btn btn-outline-primary dropdown-toggle"
              type="button"
              id="exportDropdown"
              data-bs-toggle="dropdown"
              aria-expanded="false"
              style={{ minWidth: "160px" }}
              aria-label="Export options"
            >
              Export Options
            </button>
            <ul className="dropdown-menu" aria-labelledby="exportDropdown">
              <li>
                <button className="dropdown-item" onClick={exportToPDF}>
                  Export to PDF
                </button>
              </li>
              <li>
                <button className="dropdown-item" onClick={downloadGraph}>
                  Download Graph
                </button>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {noData ? (
        <div className="text-center mt-5 py-5 fw-semibold text-muted">
          No usage data available for the selected period
        </div>
      ) : (
        <div id="employee-asset-usage" className="w-full h-96">
          <Chart options={options} series={series} type="line" height="100%" />
        </div>
      )}

      {chartData.employeeData.length > 0 && (
        <div className="d-flex align-items-center justify-content-center mt-3">
          <span className="round-8 bg-primary rounded-circle me-2 d-inline-block"></span>
          <span className="text-muted">Employee Count</span>
          <span className="round-8 bg-warning rounded-circle me-2 d-inline-block ms-3"></span>
          <span className="text-muted">Vehicle Count</span>
        </div>
      )}
    </>
  );
};

export default EmployeeAssetUsageChart;